import { AndroidBiometryStrength, BiometricAuth, CheckBiometryResult } from "@aparajita/capacitor-biometric-auth";
import { Preferences } from "@capacitor/preferences";
import { defineStore } from "pinia";
import { UserApi } from "../api/userApi";
import { Center } from "../user";
import { useUserManagerStore } from "./userManagerStore";
import { Lang } from "../utils/Languages";
import { useSnackbarStore } from "./snackbarStore";
import { useI18n } from "vue-i18n";

export type SecurityType = 'biometric' | 'password' | 'none';

export type ThemeType = 'light' | 'dark' | 'system';

export interface ProfileState {
    authenticated: boolean,
    securityType: SecurityType,
    prompting: boolean,
    showPromptPassword: 'new' | 'confirm' | 'validate' | null,
    newPassword: string | null
    passwordCheckAttempts: number
    passwordConfirmAttempts: number
    passwordMaxAttempts: number
    language: Lang | null
    theme: ThemeType
    _onThemeChange: ((theme: 'light' | 'dark') => void) | null
    _systemThemeCleanup: (() => void) | null
}

export const useProfileStore = defineStore('profile', {
    state: (): ProfileState => ({
        securityType: 'none',
        authenticated: false,
        prompting: false,
        showPromptPassword: null,
        newPassword: null,
        passwordCheckAttempts: 0,
        passwordConfirmAttempts: 0,
        passwordMaxAttempts: 5,
        language: null,
        theme: 'system',
        _onThemeChange: null,
        _systemThemeCleanup: null,
    }),

    actions: {
        load: async function () {
            const securityType = await Preferences.get({ key: 'securityType' })
            if (securityType.value) {
                this.securityType = securityType.value as SecurityType
            }

            const savedTheme = await Preferences.get({ key: 'theme' })
            if (savedTheme.value && ['light', 'dark', 'system'].includes(savedTheme.value)) {
                this.theme = savedTheme.value as ThemeType
            } else {
                this.theme = 'system'
            }
            this.applyTheme()
            this.watchSystemTheme()
        },

        setSecurityType: async function (securityType: SecurityType) {
            if (this.securityType === securityType) { return true }
            // Prompt the user to confirm the security type change with the new security type
            // before setting the new security type

            // Check if the device is secure before setting the security type
            if (securityType === 'biometric') {
                const available = await BiometricAuth.checkBiometry()
                if (!available.deviceIsSecure) {
                    throw new Error('Device is not secure')
                }
            }

            if (securityType === 'password') {
                await this.savePassword(null)
            }

            const validated = await this.promptSecurityType(securityType, true)
            if (!validated) {
                return
            }
            
            await this.saveSecurityType(securityType)
            
            const { t } = useI18n()

            const snackStore = useSnackbarStore()
            snackStore.addSnackbar(t('profile.security.type_changed'), 'success')
        },

        promptSecurityType: async function (securityType?: SecurityType, force?: boolean) {
            force = force || false;
            let success: boolean = false

            if (!securityType) {
                securityType = this.securityType;
            }

            if (force) {
                // Remove the last confirmed date to force the user to authenticate
                await Preferences.remove({ key: 'security.lastConfirm' })
            }

            // Security alive duration (1 second) to prevent multiple prompts
            const lastConfirmed = await Preferences.get({ key: 'security.lastConfirm' })
            if (lastConfirmed.value) {
                const lastConfirmedDate = new Date(lastConfirmed.value)
                const now = new Date()
                const diff = now.getTime() - lastConfirmedDate.getTime()
                if (diff < 1000) {
                    return true;
                }
            }

            switch (securityType) {
                case 'biometric':
                    success = await this.promptBiometric()
                    break;
                case 'password':
                    // Prompt for password
                    success = await this.promptPassword()
                    break;
                case 'none':
                    return true;
                default:
                    break;
            }

            if (success) {
                await Preferences.set({ key: 'security.lastConfirm', value: new Date().toISOString() })
            }

            return success
        },

        updateBiometryInfo: async function (info: CheckBiometryResult) {
            if (this.authenticated) { return true };
            if (!info.deviceIsSecure) { return false };

            if (info.deviceIsSecure && !this.authenticated) {
                try {
                    await this.promptBiometric()
                } catch (error) {
                    console.error('Error authenticating')
                }
            } else {
                console.log('Device is not secure or already authenticated')
            }
        },

        promptBiometric: async function () {
            try {
                this.prompting = true;
                const available = await BiometricAuth.checkBiometry()
                if (!available.deviceIsSecure) {
                    throw new Error('Device is not secure')
                }

                await BiometricAuth.authenticate({
                    androidConfirmationRequired: true,
                    androidBiometryStrength: AndroidBiometryStrength.strong,
                    allowDeviceCredential: true
                })
            } catch (error) {
                return false
            }

            return true;
        },

        promptPassword: async function () {
            const password = await Preferences.get({ key: 'pinPassword' })
            this.prompting = true;
            if (!password.value) {
                // Password still needs to be set and confirmed
                await this.promptNewPassword()
                return false;
            }

            // TODO prompt for password
            this.showPromptPassword = 'validate';
            return false;
        },

        promptNewPassword: async function () {
            this.showPromptPassword = 'new';
            this.newPassword = null;
            return true;
        },

        closePromptNewPassword: async function () {
            this.showPromptPassword = null;
            this.newPassword = null;
        },

        setPassword: async function (password: string) {
            if (this.showPromptPassword === 'new') {
                this.passwordConfirmAttempts = 0;
                this.newPassword = password;
                this.showPromptPassword = 'confirm';

                // Password still needs to be confirmed
                return false;
            }

            if (this.showPromptPassword !== 'confirm') {
                throw new Error('promptpassword.confirm.password_confirm_error')
            }


            if (!this.newPassword) {
                throw new Error('promptpassword.confirm.new_password_required')
            }


            // Confirm password
            this.passwordConfirmAttempts++;


            if (this.passwordConfirmAttempts > this.passwordMaxAttempts) {
                this.showPromptPassword = 'new';
                throw new Error('promptpassword.confirm.attempts_exceeded')
            }

            if (this.newPassword !== password) {
                throw new Error('promptpassword.confirm.mismatch')
            }

            await this.savePassword(this.newPassword)
            await this.saveSecurityType('password')

            this.showPromptPassword = null;
            this.newPassword = null;
            this.passwordConfirmAttempts = 0;

            return true;
        },

        saveSecurityType: async function (securityType: SecurityType) {
            await Preferences.set({ key: 'securityType', value: securityType })
            this.securityType = securityType
        },

        savePassword: async function (password: string | null) {
            if (!password) {
                await Preferences.remove({ key: 'pinPassword' })
                return true;
            }


            await Preferences.set({ key: 'pinPassword', value: password })
            return true;
        },
        checkPassword: async function (password: string | null) {
            const storedPassword = await Preferences.get({ key: 'pinPassword' })
            if (!storedPassword.value) {
                return false
            }

            this.passwordCheckAttempts++

            const success = storedPassword.value === password

            if (!success) {
                throw new Error('promptpassword.validate.invalid_password')
            }

            this.authenticated = success
            this.passwordCheckAttempts = 0

            return this.authenticated
        },


        async saveCenterUserPreferences(center: Center) {
            const userManagerStore = useUserManagerStore()
            await Preferences.set({
                key: "centers",
                value: JSON.stringify(userManagerStore.user?.centers)
            })

            const resp = await UserApi.saveUserCenterPreferences(center.preferences)

            if (resp instanceof Error) {
                return false
            }

            return true
        },

        async deleteCenter(centerId: number) {
            const resp = await UserApi.deleteUserCenter(centerId)

            if (resp instanceof Error) {
                return false;
            }

            return true;
        },

        async setLanguage(language: string) {
            await Preferences.set({ key: 'language', value: language })
            this.language = new Lang(language)
        },

        async loadLanguage() {
            let language = await Preferences.get({ key: 'language' })
            let code = "";
            if (!language.value) {
                code = navigator.language.split('-')[0].toLocaleLowerCase()
            } else {
                code = language.value
            }

            let lang: Lang;

            try {
                lang = new Lang(code)
            } catch (error) {
                lang = new Lang('en')
            }
            this.language = lang
        },

        async setTheme(theme: ThemeType) {
            this.theme = theme
            await Preferences.set({ key: 'theme', value: theme })
            this.applyTheme()
            this.watchSystemTheme()
        },

        applyTheme() {
            const effective = this.getEffectiveTheme()
            if (effective === 'dark') {
                document.documentElement.classList.add('dark')
            } else {
                document.documentElement.classList.remove('dark')
            }
            if (this._onThemeChange) {
                this._onThemeChange(effective)
            }
        },

        getEffectiveTheme(): 'light' | 'dark' {
            if (this.theme === 'system') {
                return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
            }
            return this.theme as 'light' | 'dark'
        },

        watchSystemTheme() {
            // Cleanup previous listener
            if (this._systemThemeCleanup) {
                this._systemThemeCleanup()
                this._systemThemeCleanup = null
            }

            if (this.theme !== 'system') return

            const mql = window.matchMedia('(prefers-color-scheme: dark)')
            const handler = () => this.applyTheme()
            mql.addEventListener('change', handler)
            this._systemThemeCleanup = () => mql.removeEventListener('change', handler)
        }
    }
})